import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Bot, LayoutTemplate, PlugZap } from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WhatsAppSettings } from "@/pages/whatsapp/WhatsAppSettings";
import { AutomationSettings } from "@/pages/whatsapp/AutomationSettings";

type Tab = "connection" | "templates" | "automation";

const TABS: { key: Tab; label: string; icon: React.ElementType; hint: string }[] = [
  { key: "connection", label: "Connection", icon: PlugZap, hint: "Company number, service key and message link" },
  { key: "templates", label: "Templates", icon: LayoutTemplate, hint: "Pre-approved messages for starting conversations" },
  { key: "automation", label: "Automation", icon: Bot, hint: "Routing, alerts, auto-replies, quotation messages, costs" },
];

/** Settings → WhatsApp: connection, templates and automation as separate tabs (admins) */
export function WhatsAppSettingsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") as Tab | null;
  const tab: Tab = TABS.some((t) => t.key === requested) ? requested! : "connection";
  const [connected, setConnected] = useState<boolean | null>(null);

  const check = useCallback(() => {
    api
      .get("/api/WhatsApp/Settings")
      .then((res) => setConnected(Boolean(unwrapObject<any>(res)?.connected)))
      .catch(() => setConnected(false));
  }, []);

  useEffect(check, [check]);

  return (
    <div className="space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center gap-3">
        <WhatsAppIcon className="size-8" />
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            WhatsApp
            {connected !== null && (
              <span
                className={cn(
                  "text-[11px] font-medium px-2 py-0.5 rounded-full",
                  connected ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                )}
              >
                {connected ? "Connected" : "Not connected"}
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">{TABS.find((t) => t.key === tab)!.hint}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setParams(t.key === "connection" ? {} : { tab: t.key }, { replace: true })}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
              tab === t.key ? "border-[#25D366] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <t.icon className="size-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "connection" && <WhatsAppSettings section="connection" onSaved={check} />}
      {tab === "templates" &&
        (connected === false ? (
          <NotConnected onGo={() => setParams({}, { replace: true })} what="manage templates" />
        ) : (
          <WhatsAppSettings section="templates" onSaved={check} />
        ))}
      {tab === "automation" && connected !== null && <AutomationSettings connected={connected} />}
    </div>
  );
}

function NotConnected({ onGo, what }: { onGo: () => void; what: string }) {
  return (
    <div className="bg-card border rounded-2xl p-8 text-center space-y-3">
      <WhatsAppIcon className="size-10 mx-auto" />
      <p className="text-sm text-muted-foreground">Connect the company WhatsApp number first to {what}.</p>
      <button type="button" onClick={onGo} className="px-4 py-1.5 rounded-full bg-[#25D366] text-white text-sm font-medium">
        Go to Connection
      </button>
    </div>
  );
}

export default WhatsAppSettingsPage;
